import {
  Box,
  Divider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { usePartnerProfileConfig } from '../Enrollment/usePartnerProfileConfig';
import { PartnerProductLine, RebateType } from '../../../../common/models/Partner';
import { SubscriptionTier } from '../../../../common/models/Subscription';
import { useAppSelector } from '../../../../store/hooks';
import { selectMemberInformation } from '../../../../store/slices/onboardingSlice';
import { TestIds } from '../../../../assets/testIds';
import { useOptimizelyFlag } from '../../../../common/hooks/useOptimizelyFlag';
import { RebateMatrixFlag } from '../../../../common/OptimizelyFlagsConstants';
import { AccessResource, useHasAccess } from '../../../../common/hooks/useHasAccess';

// Finds rebate rate matching member's subscription tier and formats as percentage or dollar amount
const formatRebateValue = (
  productLine: PartnerProductLine,
  subscriptionTier?: SubscriptionTier,
): string | null => {
  const tierCode = subscriptionTier?.toLowerCase();
  const rate = subscriptionTier ?
    productLine.rebateRates.find((r) => r.rebateTierCode?.toLowerCase() === tierCode) :
    productLine.rebateRates[0];

  if (!rate) return null;
  const rebateType = productLine.rebateType?.toLowerCase();
  if (rebateType === RebateType.PERCENTAGE &&
    rate.rebatePercentage !== null &&
    rate.rebatePercentage !== undefined) {
    return `${rate.rebatePercentage}%`;
  }

  if (rebateType === RebateType.AMOUNT &&
    rate.rebateAmount !== null &&
    rate.rebateAmount !== undefined) {
    return `$${Number(rate.rebateAmount).toFixed(2)}`;
  }

  return null;
};

// Rebate-matrix-specific tier config — NOT a general membership tier ordering.
// Display names and upgrade targets here follow the rebate matrix rules (SN-961,
// per product spec): AM/Edge both point to SRT, and RCC/CC skip Advanced to go
// straight to Alliance. Do not merge with the partner access maps in
// common/utils/MembershipTierAccess.
//
// rateLookupTier: when set, rate lookups for this tier use a different tier code.
// PIN (Pinnacle) intentionally uses SNA rates — if a partner ever configures genuine
// PIN-coded rates, this override would discard them. Confirmed product decision (SN-961).
// Similarly ADVP uses ADV rates by product spec.
//
// This table follows the Rebates chart on product's "SN Logic on the Edge platform"
// page, a different chart from the Partners one MembershipTierAccess encodes, which is
// why AM and RCC sit differently in the two. Keep them separate.
type RebateMatrixTierInfo = {
  displayNameKey: string;
  nextTier?: SubscriptionTier;
  rateLookupTier?: SubscriptionTier;
};
const REBATE_MATRIX_TIER_CONFIG: Partial<Record<SubscriptionTier, RebateMatrixTierInfo>> = {
  // Edge earns no rebates of its own, so its own column is always empty, but it
  // still has a row on the Rebate Matrix chart pointing at Roundtable with an
  // "Upgrade" button (SN-996). Without this entry Edge resolved to no next tier and
  // never reached that CTA.
  [SubscriptionTier.EDGE]: { displayNameKey: 'partnerDetails.tierName.edge', nextTier: SubscriptionTier.SRT },
  [SubscriptionTier.AM]: { displayNameKey: 'partnerDetails.tierName.association', nextTier: SubscriptionTier.SRT },
  [SubscriptionTier.SRT]: { displayNameKey: 'partnerDetails.tierName.roundtable', nextTier: SubscriptionTier.ADV },
  [SubscriptionTier.RCC]: { displayNameKey: 'partnerDetails.tierName.coalition', nextTier: SubscriptionTier.SNA },
  [SubscriptionTier.CC]: { displayNameKey: 'partnerDetails.tierName.coalition', nextTier: SubscriptionTier.SNA },
  [SubscriptionTier.ADV]: { displayNameKey: 'partnerDetails.tierName.advanced', nextTier: SubscriptionTier.SNA },
  [SubscriptionTier.ADVP]: {
    displayNameKey: 'partnerDetails.tierName.advanced',
    nextTier: SubscriptionTier.SNA,
    rateLookupTier: SubscriptionTier.ADV,
  },
  [SubscriptionTier.SNA]: { displayNameKey: 'partnerDetails.tierName.alliance' },
  [SubscriptionTier.PIN]: { displayNameKey: 'partnerDetails.tierName.pinnacle', rateLookupTier: SubscriptionTier.SNA },
};

// Returns undefined for unknown/unrecognized tiers rather than falling back to a
// tier name that would mislabel the member's own column.
const getTierI18nKey = (tier?: SubscriptionTier): string | undefined => {
  if (!tier) return undefined;
  return REBATE_MATRIX_TIER_CONFIG[tier]?.displayNameKey;
};

const getNextTier = (tier?: SubscriptionTier): SubscriptionTier | undefined => {
  if (!tier) return undefined;
  return REBATE_MATRIX_TIER_CONFIG[tier]?.nextTier;
};

// Returns the tier code for rate lookups. PIN → SNA, ADVP → ADV per product spec.
// Intentional override, not a fallback; see rateLookupTier comment in the config block.
const getEffectiveRebateTier = (tier?: SubscriptionTier): SubscriptionTier | undefined => {
  if (!tier) return undefined;
  return REBATE_MATRIX_TIER_CONFIG[tier]?.rateLookupTier ?? tier;
};

type PartnerRebateMatrixSectionProps = {
  onContactSales?: () => void;
  onUpgradeNow?: () => void;
  hasContactedSales?: boolean;
  // The partner requires a higher tier than the member's. The member's own rates are
  // then withheld and the matrix previews their next tier's rates greyed out, with a
  // "Current Plan" footer row holding the upgrade call to action (SN-996, per QA).
  isPartnerLocked?: boolean;
};

export const PartnerRebateMatrixSection = ({
  onContactSales,
  onUpgradeNow,
  hasContactedSales = false,
  isPartnerLocked = false,
}: PartnerRebateMatrixSectionProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    decision: rebateMatrixDecision,
    isDecisionReady: isRebateMatrixDecisionReady,
  } = useOptimizelyFlag({ flag: RebateMatrixFlag });
  const partnerProfileConfig = usePartnerProfileConfig();
  const { company } = useAppSelector(selectMemberInformation);

  const rawTier = company?.subscription?.subscriptionTier;
  // A member with no subscription on record is a free Edge member. Leaving the tier
  // undefined made formatRebateValue fall back to the partner's first rate, which is
  // how a free member ended up shown another tier's (e.g. Alliance's) rebate.
  const subscriptionTier = (rawTier?.toLowerCase() ?? SubscriptionTier.EDGE) as SubscriptionTier;
  const effectiveTier = getEffectiveRebateTier(subscriptionTier);
  const nextTier = getNextTier(subscriptionTier);
  const currentTierName = t(getTierI18nKey(subscriptionTier) ?? '');
  const nextTierName = nextTier ? t(getTierI18nKey(nextTier) ?? '') : undefined;

  const hasProductLines = (partnerProfileConfig?.partnerProductLines?.length ?? 0) > 0;

  // Filter to active product lines that have a rebate rate for the member's current tier
  const productLines = useMemo(() => {
    const lines = partnerProfileConfig?.partnerProductLines;
    if (!lines) return [];
    return lines.filter(
      (pl) => pl.active && formatRebateValue(pl, effectiveTier) !== null,
    );
  }, [partnerProfileConfig?.partnerProductLines, effectiveTier]);

  // Filter to active product lines that have a rebate rate for the next tier.
  // Uses getEffectiveRebateTier(nextTier) so that if a tier with a rateLookupTier
  // is ever someone's next tier, its rates are looked up correctly.
  const nextTierProductLines = useMemo(() => {
    const lines = partnerProfileConfig?.partnerProductLines;
    if (!lines || !nextTier) return [];
    const effectiveNextTier = getEffectiveRebateTier(nextTier);
    return lines.filter(
      (pl) => pl.active && formatRebateValue(pl, effectiveNextTier) !== null,
    );
  }, [partnerProfileConfig?.partnerProductLines, nextTier]);

  // Combined product lines for the table (rows with a rate for current tier OR next tier)
  const tableProductLines = useMemo(() => {
    const lines = partnerProfileConfig?.partnerProductLines;
    if (!lines) return [];
    const currentTierIds = new Set(productLines.map((pl) => pl.id));
    const nextTierIds = new Set(nextTierProductLines.map((pl) => pl.id));
    return lines.filter(
      (pl) => pl.active && (currentTierIds.has(pl.id) || nextTierIds.has(pl.id)),
    );
  }, [partnerProfileConfig?.partnerProductLines, productLines, nextTierProductLines]);

  const hasRebatesForTier = productLines.length > 0;
  const showNextTierColumn = !!nextTier && nextTierProductLines.length > 0;
  // Gate the next-tier column and CTAs behind the flag; base matrix always renders.
  const showNextTier = isRebateMatrixDecisionReady && !!rebateMatrixDecision?.enabled;
  const effectiveShowNextTierColumn = showNextTier && showNextTierColumn;
  const shouldShowRebateMatrix = useHasAccess(AccessResource.PartnerRebateMatrix);

  // Edge and Association both have Roundtable as their next rebate tier, which is
  // sold in app, so both get "Upgrade"; every tier from Roundtable up is pointed at
  // Advanced or Alliance and needs "Contact Sales". This follows the Rebate Matrix
  // chart on "SN Logic on the Edge platform" (SN-996) - Edge used to be excluded.
  //
  // This is deliberately its own list rather than a reuse of the partner-access maps:
  // the question here is "can this member buy their way to better rebates in app", not
  // "which partners may this member see", and TIERS.md keeps those domains separate.
  const isSelfServeUpgradeTier = subscriptionTier === SubscriptionTier.EDGE ||
    subscriptionTier === SubscriptionTier.AM;
  const showUpgradeNow = isSelfServeUpgradeTier && !!onUpgradeNow;

  // Locked partner: preview the next tier only, never the member's own rates, so the
  // table can't read as rebates the member already earns here.
  const showLockedPreview = isPartnerLocked && showNextTier && nextTierProductLines.length > 0;

  // No current-tier rates → always show the upgrade section, not a partially blank table.
  // A locked partner with nothing to preview lands here too.
  const shouldShowUpgradeSection = shouldShowRebateMatrix && hasProductLines &&
    !showLockedPreview && (isPartnerLocked || !hasRebatesForTier);

  if (!shouldShowRebateMatrix || !hasProductLines) {
    return null;
  }

  if (shouldShowUpgradeSection) {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: theme.spacing(1.5),
            alignSelf: 'stretch',
          }}
        >
          <Typography variant="h4">
            {t('partnerDetails.upgradeMembership.title')}
          </Typography>
          <Typography variant="body8" color={theme.palette.grey[700]}>
            {t('partnerDetails.upgradeMembership.description')}
          </Typography>
          {/*
            Edge lands here on every partner, having no rebate rates of its own, so
            this is the only place an Edge member can be given the "Upgrade" button
            the Rebate Matrix chart calls for (SN-996). Tiers that need a sales
            conversation keep the phone number instead.
          */}
          {showUpgradeNow ? (
            <Link
              component="button"
              type="button"
              variant="labelSmProminent"
              onClick={onUpgradeNow}
              data-testid={TestIds.partnerDetailsPage.rebateMatrixUpgradeNowBtn}
            >
              {t('button.upgradeNow')}
            </Link>
          ) : (
            <Typography variant="body8" color={theme.palette.grey[700]}>
              {t('partnerDetails.upgradeMembership.callToAction')}
            </Typography>
          )}
        </Box>
        <Divider />
      </>
    );
  }

  const cellBorder = `1px solid ${theme.palette.grey[300]}`;

  const upgradeCta = showUpgradeNow ? (
    <Link
      component="button"
      type="button"
      variant="labelSmProminent"
      onClick={onUpgradeNow}
      data-testid={TestIds.partnerDetailsPage.rebateMatrixUpgradeNowBtn}
    >
      {t('button.upgradeNow')}
    </Link>
  ) : (
    <Link
      component="button"
      type="button"
      variant="labelSmProminent"
      onClick={onContactSales}
      disabled={hasContactedSales}
      sx={{ opacity: hasContactedSales ? 0.4 : 1 }}
      data-testid={TestIds.partnerDetailsPage.rebateMatrixContactSalesBtn}
    >
      {t('partnerDetails.contactSales.cta')}
    </Link>
  );

  const lockedTierHeader = (
    <Typography
      variant="labelSmProminent"
      sx={{
        opacity: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <LockOutlinedIcon
        fontSize="inherit"
        titleAccess={t('partnerDetails.tierName.locked') ?? undefined}
      />
      {nextTierName}
    </Typography>
  );

  if (showLockedPreview) {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: theme.spacing(1.5),
            alignSelf: 'stretch',
          }}
        >
          <Typography variant="h4">{t('partnerDetails.rebateMatrix')}</Typography>
          <TableContainer
            sx={{
              border: cellBorder,
              borderRadius: theme.spacing(0.5),
              overflow: 'hidden',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                  <TableCell
                    sx={{
                      borderRight: cellBorder,
                      borderBottom: cellBorder,
                      py: theme.spacing(1.25),
                      px: theme.spacing(1.5),
                    }}
                  >
                    <Typography variant="labelSmProminent">
                      {t('partnerDetails.productLine')}
                    </Typography>
                  </TableCell>
                  <TableCell
                    data-testid={TestIds.partnerDetailsPage.rebateMatrixNextTierHeader}
                    sx={{
                      borderBottom: cellBorder,
                      py: theme.spacing(1.25),
                      px: theme.spacing(1.5),
                      width: theme.spacing(23.75),
                    }}
                  >
                    {lockedTierHeader}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ '& tr:last-child td': { borderBottom: 0 } }}>
                {nextTierProductLines.map((productLine, index) => (
                  <TableRow key={productLine.id} data-testid={`partner-rebate-row-${index}`}>
                    <TableCell
                      sx={{
                        borderRight: cellBorder,
                        borderBottom: cellBorder,
                        py: theme.spacing(1.25),
                        px: theme.spacing(1.5),
                      }}
                    >
                      <Typography variant="h13">
                        {productLine.productLineName}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: cellBorder,
                        py: theme.spacing(1.25),
                        px: theme.spacing(1.5),
                      }}
                    >
                      <Typography variant="labelSmProminent" sx={{ opacity: 0.5 }}>
                        {formatRebateValue(productLine, getEffectiveRebateTier(nextTier))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell
                    sx={{
                      borderRight: cellBorder,
                      borderTop: cellBorder,
                      py: theme.spacing(1.25),
                      px: theme.spacing(1.5),
                    }}
                  >
                    <Typography variant="labelSmProminent">
                      {t('partnerDetails.currentPlan')}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      borderTop: cellBorder,
                      py: theme.spacing(1.25),
                      px: theme.spacing(1.5),
                    }}
                  >
                    {upgradeCta}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Box>
        <Divider />
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: theme.spacing(1.5),
          alignSelf: 'stretch',
        }}
      >
        <Typography variant="h4">{t('partnerDetails.rebateMatrix')}</Typography>
        <TableContainer
          sx={{
            border: cellBorder,
            borderRadius: theme.spacing(0.5),
            overflow: 'hidden',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                <TableCell
                  sx={{
                    borderRight: cellBorder,
                    borderBottom: cellBorder,
                    py: theme.spacing(1.25),
                    px: theme.spacing(1.5),
                  }}
                >
                  <Typography variant="labelSmProminent">
                    {t('partnerDetails.productLine')}
                  </Typography>
                </TableCell>
                <TableCell
                  data-testid={TestIds.partnerDetailsPage.rebateMatrixCurrentTierHeader}
                  sx={{
                    borderBottom: cellBorder,
                    borderRight: effectiveShowNextTierColumn ? cellBorder : undefined,
                    py: theme.spacing(1.25),
                    px: theme.spacing(1.5),
                    width: theme.spacing(23.75),
                  }}
                >
                  <Typography variant="labelSmProminent">
                    {currentTierName}
                  </Typography>
                </TableCell>
                {effectiveShowNextTierColumn && (
                  <TableCell
                    data-testid={TestIds.partnerDetailsPage.rebateMatrixNextTierHeader}
                    sx={{
                      borderBottom: cellBorder,
                      py: theme.spacing(1.25),
                      px: theme.spacing(1.5),
                      width: theme.spacing(23.75),
                    }}
                  >
                    {lockedTierHeader}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody sx={{ '& tr:last-child td': { borderBottom: 0 } }}>
              {tableProductLines.map((productLine, index) => (
                <TableRow key={productLine.id} data-testid={`partner-rebate-row-${index}`}>
                  <TableCell
                    sx={{
                      borderRight: cellBorder,
                      borderBottom: cellBorder,
                      py: theme.spacing(1.25),
                      px: theme.spacing(1.5),
                    }}
                  >
                    <Typography variant="h13">
                      {productLine.productLineName}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: cellBorder,
                      borderRight: effectiveShowNextTierColumn ? cellBorder : undefined,
                      py: theme.spacing(1.25),
                      px: theme.spacing(1.5),
                    }}
                  >
                    <Typography variant="labelSmProminent">
                      {formatRebateValue(productLine, effectiveTier)}
                    </Typography>
                  </TableCell>
                  {effectiveShowNextTierColumn && (
                    <TableCell
                      sx={{
                        borderBottom: cellBorder,
                        py: theme.spacing(1.25),
                        px: theme.spacing(1.5),
                      }}
                    >
                      <Typography variant="labelSmProminent" sx={{ opacity: 0.5 }}>
                        {formatRebateValue(productLine, getEffectiveRebateTier(nextTier))}
                      </Typography>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
            {effectiveShowNextTierColumn && (
              <TableFooter>
                <TableRow>
                  <TableCell
                    sx={{
                      borderRight: cellBorder,
                      borderTop: cellBorder,
                      py: theme.spacing(1.25),
                      px: theme.spacing(1.5),
                    }}
                  />
                  <TableCell
                    sx={{
                      borderRight: cellBorder,
                      borderTop: cellBorder,
                      py: theme.spacing(1.25),
                      px: theme.spacing(1.5),
                    }}
                  >
                    <Typography variant="labelSmProminent">
                      {t('partnerDetails.currentPlan')}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      borderTop: cellBorder,
                      py: theme.spacing(1.25),
                      px: theme.spacing(1.5),
                    }}
                  >
                    {upgradeCta}
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Box>
      <Divider />
    </>
  );
};
